import React, { useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { ArrowBack, PersonAdd } from '@mui/icons-material';
import { colors } from '../theme/modernTheme';
import { showSuccess, showError } from '../utils/toast';
import TransactionForm from '../components/TransactionForm';
import { useAuth } from '../context/AuthContext';
import { useLoading } from '../context/LoadingContext';
import { fetchReceiptPreview } from '../utils/receiptHelpers';
import ReceiptPreviewModal from '../components/ReceiptPreviewModal';
import { transactionsAPI } from '../services/api';

export default function TransactionDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const reminderId = searchParams.get('reminder_id');
  const formRef = useRef(null);

  const initialMode = id && id !== 'new' && searchParams.get('mode') !== 'edit' ? 'view' : 'edit';
  const [mode, setMode] = useState(initialMode);

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHTML, setPreviewHTML] = useState('');
  const [previewTransaction, setPreviewTransaction] = useState(null);

  const canEdit = user?.role === 'ADMIN' || user?.role === 'STAFF';
  const canDelete = user?.role === 'ADMIN';
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  const handleEditClick = () => {
    setMode('edit');
    navigate(`/transactions/${id}?mode=edit`, { replace: true });
  };

  const handleAddContact = () => {
    // Call the form's handleAddContact method via ref
    if (formRef.current && formRef.current.handleAddContact) {
      formRef.current.handleAddContact();
    }
  };

  // Handle preview receipt
  const handlePreviewReceipt = async () => {
    try {
      showLoading();
      const data = await fetchReceiptPreview(id);
      setPreviewHTML(data.html);
      setPreviewTransaction({
        id: id,
        ...data.transaction,
      });
      setPreviewOpen(true);
    } catch (error) {
      console.error('Preview error:', error);
      showError('Failed to load receipt preview');
    } finally {
      hideLoading();
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewHTML('');
    setPreviewTransaction(null);
  };

  // Handle download PDF
  const handleDownloadPDF = async (transaction) => {
    try {
      showLoading();
      const blobData = await transactionsAPI.downloadReceiptPDF(transaction.id);
      const blob = new Blob([blobData], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${transaction.receipt_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess('Receipt downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      showError('Failed to download receipt');
    } finally {
      hideLoading();
    }
  };

  // Handle send email
  const handleSendEmail = async (transaction) => {
    try {
      showLoading();
      await transactionsAPI.sendReceiptEmail(transaction.id);
      showSuccess('Receipt sent via email successfully');
    } catch (error) {
      console.error('Email error:', error);
      showError('Failed to send receipt via email');
    } finally {
      hideLoading();
    }
  };

  // Handle send WhatsApp
  const handleSendWhatsApp = async (transaction) => {
    try {
      showLoading();
      await transactionsAPI.sendReceiptWhatsApp(transaction.id);
      showSuccess('Receipt sent via WhatsApp successfully');
    } catch (error) {
      console.error('WhatsApp error:', error);
      showError('Failed to send receipt via WhatsApp');
    } finally {
      hideLoading();
    }
  };

  const handleSuccess = () => {
    showSuccess(
      id && id !== 'new' ? 'Transaction updated successfully' : 'Transaction created successfully',
    );
    navigate('/transactions');
  };

  const handleCancel = () => {
    if (id && id !== 'new') {
      setMode('view');
      navigate(`/transactions/${id}`, { replace: true });
    } else {
      navigate('/transactions');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={() => navigate('/transactions')}
            size="small"
            sx={{
              '&:hover': { color: colors.primary.main },
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.2rem',
              fontWeight: 700,
              color: colors.text.primary,
            }}
          >
            {isViewMode
              ? 'Transaction Details'
              : id && id !== 'new'
                ? 'Edit Transaction'
                : 'Create Transaction'}
          </Typography>
        </Box>

        {isViewMode ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Preview Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={handlePreviewReceipt}
              sx={{
                textTransform: 'none',
                borderRadius: '4px',
                border: `1px solid ${colors.primary.main}`,
                color: colors.primary.main,
                '&:hover': {
                  textDecoration: 'none',
                  backgroundColor: `${colors.action.hover} !important`,
                  borderColor: colors.primary.dark,
                },
              }}
            >
              Preview & Download Receipt
            </Button>
            {canEdit && (
              <Button
                variant="text"
                size="small"
                onClick={handleEditClick}
                sx={{
                  textTransform: 'none',
                  borderRadius: '4px',
                  border: `1px solid ${colors.primary.main}`,
                  color: colors.primary.main,
                  '&:hover': {
                    textDecoration: 'none',
                    backgroundColor: `${colors.action.hover} !important`,
                  },
                }}
              >
                Edit
              </Button>
            )}
          </Box>
        ) : (
          isEditMode &&
          (id === 'new' || !id) &&
          !reminderId && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAdd />}
              onClick={handleAddContact}
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                borderColor: colors.primary.main,
                color: colors.primary.main,
                '&:hover': {
                  borderColor: colors.primary.dark,
                  backgroundColor: `${colors.primary.main}10`,
                },
              }}
            >
              Add New Contact
            </Button>
          )
        )}
      </Box>

      {/* Form */}
      <TransactionForm
        ref={formRef}
        transactionId={id !== 'new' ? id : null}
        reminderId={reminderId}
        mode={mode}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />

      {/* Receipt Preview Modal */}
      <ReceiptPreviewModal
        open={previewOpen}
        onClose={handlePreviewClose}
        content={previewHTML}
        hasEmail={!!previewTransaction?.primary_email}
        hasPhone={!!previewTransaction?.phone_1}
        donorName={previewTransaction?.donor_name || ''}
        onDownload={() => handleDownloadPDF(previewTransaction)}
        onSendEmail={() => handleSendEmail(previewTransaction)}
        onSendWhatsApp={() => handleSendWhatsApp(previewTransaction)}
        emailTemplate={previewTransaction?.email_template_id || ''}
        whatsappTemplate={previewTransaction?.whatsapp_template_id || ''}
      />
    </Box>
  );
}
